/* OpenFOAM Blockly blocks – schema-free edition */
import * as Blockly from 'blockly';
import schema from './openfoam.schema.json';

// Constants
const PREFIX = 'controlDict';
const BLOCK_COLOR = 230;

// Default FoamFile entries for controlDict
const DEFAULT_FOAMFILE_ENTRIES = {
  version: '2.0',
  format: 'ascii',
  class: 'dictionary',
  location: 'system',
  object: 'controlDict'
};

// Export dictionary name and prefix for registry
export default {
  dictName: 'controlDict',
  prefix: PREFIX,
  schema: schema.dictionaries.controlDict,
  registerBlocks,
  generateToolbox,
  getBlocks: () => Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)),
  findMatchingBlockType: (component) => findMatchingBlockType(component),
};

// Define validation functions for each key
const VALIDATORS = {
  applicationClass: (value) => {
    const validClasses = ['icoFoam', 'simpleFoam', 'pimpleFoam', 'rhoCentralFoam'];
    return validClasses.includes(value) ? value : 'icoFoam';
  },
  startFrom: (value) => {
    const validStarts = ['startTime', 'latestTime', 'firstTime'];
    return validStarts.includes(value) ? value : 'startTime';
  },
  startTime: (value) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 ? num : 0;
  },
  stopAt: (value) => {
    const validStops = ['endTime', 'writeNow', 'nextWrite', 'adjustableRunTime'];
    return validStops.includes(value) ? value : 'endTime';
  },
  endTime: (value) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 ? num : 1000;
  },
  deltaT: (value) => {
    const num = Number(value);
    return !isNaN(num) && num > 0 ? num : 1;
  },
  writeControl: (value) => {
    const validControls = ['timeStep', 'adjustableRunTime', 'runTime', 'clockTime', 'cpuTime'];
    return validControls.includes(value) ? value : 'timeStep';
  },
  writeInterval: (value) => {
    const num = Number(value);
    return !isNaN(num) && num > 0 ? num : 1;
  },
  purgeWrite: (value) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 ? num : 0;
  },
  writeFormat: (value) => {
    const validFormats = ['ascii', 'binary'];
    return validFormats.includes(value) ? value : 'ascii';
  },
  writePrecision: (value) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 && num <= 20 ? num : 6;
  },
  writeCompression: (value) => {
    const validCompressions = ['on', 'off', 'true', 'false'];
    return validCompressions.includes(value.toLowerCase()) ? value : 'off';
  },
  timeFormat: (value) => {
    const validFormats = ['general', 'scientific', 'fixed'];
    return validFormats.includes(value) ? value : 'general';
  },
  timePrecision: (value) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 && num <= 20 ? num : 6;
  },
  runTimeModifiable: (value) => {
    const validValues = ['true', 'false', 'yes', 'no'];
    return validValues.includes(value.toLowerCase()) ? value : 'true';
  },
  cycleWrite: (value) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 ? num : 0;
  },
  adjustTimeStep: (value) => {
    const validValues = ['yes', 'no', 'true', 'false'];
    return validValues.includes(value.toLowerCase()) ? value : 'no';
  },
  maxCo: (value) => {
    const num = Number(value);
    return !isNaN(num) && num > 0 ? num : 1;
  },
  maxDeltaT: (value) => {
    const num = Number(value);
    return !isNaN(num) && num > 0 ? num : 1;
  },
  writeOption: (value) => {
    const validOptions = ['timeStep', 'runTime', 'adjustableRunTime', 'clockTime', 'cpuTime'];
    return validOptions.includes(value) ? value : 'timeStep';
  },
  include: (value) => {
    // Allow any string value for include statements
    return typeof value === 'string' ? value : '';
  },
  application: (value) => {
    const validApplications = ['icoFoam', 'simpleFoam', 'pimpleFoam', 'rhoCentralFoam', 'buoyantFoam', 'interFoam'];
    return validApplications.includes(value) ? value : 'icoFoam';
  },
  solver: (value) => {
    const validSolvers = ['GAMG', 'PCG', 'PBiCG', 'smoothSolver', 'diagonal'];
    return validSolvers.includes(value) ? value : 'GAMG';
  },
};

// Define keyword configurations with validation
const KEYWORD_CONFIGS = [
  { 
    key: 'applicationClass', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'applicationClass name',
    validator: VALIDATORS.applicationClass,
    defaultValue: 'icoFoam'
  },
  { 
    key: 'startFrom', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Start time specification',
    validator: VALIDATORS.startFrom,
    defaultValue: 'startTime'
  },
  { 
    key: 'startTime', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Start time',
    validator: VALIDATORS.startTime,
    defaultValue: 0
  },
  { 
    key: 'stopAt', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Stop time specification',
    validator: VALIDATORS.stopAt,
    defaultValue: 'endTime'
  },
  { 
    key: 'endTime', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'End time',
    validator: VALIDATORS.endTime,
    defaultValue: 1000
  },
  { 
    key: 'deltaT', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Time step',
    validator: VALIDATORS.deltaT,
    defaultValue: 1
  },
  { 
    key: 'writeControl', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Write control method',
    validator: VALIDATORS.writeControl,
    defaultValue: 'timeStep'
  },
  { 
    key: 'writeInterval', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Write interval',
    validator: VALIDATORS.writeInterval,
    defaultValue: 1
  },
  { 
    key: 'purgeWrite', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Purge write',
    validator: VALIDATORS.purgeWrite,
    defaultValue: 0
  },
  { 
    key: 'writeFormat', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Write format',
    validator: VALIDATORS.writeFormat,
    defaultValue: 'ascii'
  },
  { 
    key: 'writePrecision', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Write precision',
    validator: VALIDATORS.writePrecision,
    defaultValue: 6
  },
  { 
    key: 'writeCompression', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Write compression',
    validator: VALIDATORS.writeCompression,
    defaultValue: 'off'
  },
  { 
    key: 'timeFormat', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Time format',
    validator: VALIDATORS.timeFormat,
    defaultValue: 'general'
  },
  { 
    key: 'timePrecision', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Time precision',
    validator: VALIDATORS.timePrecision,
    defaultValue: 6
  },
  { 
    key: 'runTimeModifiable', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Run time modifiable',
    validator: VALIDATORS.runTimeModifiable,
    defaultValue: 'true'
  },
  { 
    key: 'cycleWrite', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'cycle write',
    validator: VALIDATORS.cycleWrite,
    defaultValue: 0
  },
  { 
    key: 'adjustTimeStep', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Adjust time step based on maxCo',
    validator: VALIDATORS.adjustTimeStep,
    defaultValue: 'no'
  },
  { 
    key: 'maxCo', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Maximum Courant number',
    validator: VALIDATORS.maxCo,
    defaultValue: 1
  },
  { 
    key: 'maxDeltaT', 
    shadow: `${PREFIX}_value_number`, 
    check: 'Number', 
    tooltip: 'Maximum time step',
    validator: VALIDATORS.maxDeltaT,
    defaultValue: 1
  },
  { 
    key: 'writeOption', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Write option control method',
    validator: VALIDATORS.writeOption,
    defaultValue: 'timeStep'
  },
  { 
    key: 'include', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Include statement for external files',
    validator: VALIDATORS.include,
    defaultValue: ''
  },
  { 
    key: 'application', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Application solver name',
    validator: VALIDATORS.application,
    defaultValue: 'icoFoam'
  },
  { 
    key: 'solver', 
    shadow: `${PREFIX}_value_string`, 
    check: 'String', 
    tooltip: 'Solver type for linear systems',
    validator: VALIDATORS.solver,
    defaultValue: 'GAMG'
  },
];

// Define component configurations for matching
const COMPONENT_CONFIGS = {
  dictionary: {
    FoamFile: {
      blockType: `${PREFIX}_foamfile`,
      entries: {
        version: { type: 'keyValue', value: 2 },
        format: { type: 'keyValue', value: 'ascii' },
        class: { type: 'keyValue', value: 'dictionary' },
        location: { type: 'keyValue', value: 'system' },
        object: { type: 'keyValue', value: 'controlDict' }
      }
    }
  },
  keyValue: {
    // Add other key-value pairs specific to controlDict
    applicationClass: { blockType: `${PREFIX}_applicationClass` },
    startFrom: { blockType: `${PREFIX}_startFrom` },
    startTime: { blockType: `${PREFIX}_startTime` },
    stopAt: { blockType: `${PREFIX}_stopAt` },
    endTime: { blockType: `${PREFIX}_endTime` },
    deltaT: { blockType: `${PREFIX}_deltaT` },
    writeControl: { blockType: `${PREFIX}_writeControl` },
    writeInterval: { blockType: `${PREFIX}_writeInterval` },
    purgeWrite: { blockType: `${PREFIX}_purgeWrite` },
    writeFormat: { blockType: `${PREFIX}_writeFormat` },
    writePrecision: { blockType: `${PREFIX}_writePrecision` },
    writeCompression: { blockType: `${PREFIX}_writeCompression` },
    timeFormat: { blockType: `${PREFIX}_timeFormat` },
    timePrecision: { blockType: `${PREFIX}_timePrecision` },
    runTimeModifiable: { blockType: `${PREFIX}_runTimeModifiable` },
    cycleWrite: { blockType: `${PREFIX}_cycleWrite` },
    adjustTimeStep: { blockType: `${PREFIX}_adjustTimeStep` },
    maxCo: { blockType: `${PREFIX}_maxCo` },
    maxDeltaT: { blockType: `${PREFIX}_maxDeltaT` },
    writeOption: { blockType: `${PREFIX}_writeOption` },
    include: { blockType: `${PREFIX}_include` },
    application: { blockType: `${PREFIX}_application` },
    solver: { blockType: `${PREFIX}_solver` },
  }
};

/**
 * Utility to create and initialize a Blockly block
 * @param {Blockly.Workspace} workspace - Target workspace
 * @param {string} blockType - Block type to create
 * @returns {Blockly.Block} Initialized block
 */
function createBlock(workspace, blockType) {
  try {
    if (!Blockly.Blocks[blockType]) {
      throw new Error(`Block type ${blockType} not defined`);
    }
    const block = workspace.newBlock(blockType);
    block.initSvg();
    block.render();
    return block;
  } catch (error) {
    console.error(`Error creating block ${blockType}:`, error);
    return null;
  }
}

/**
 * Utility to connect a value block to a block's input
 * @param {Blockly.Block} block - Target block
 * @param {Blockly.Block} valueBlock - Value block to connect
 * @param {string} entryKey - Key of the entry for logging
 * @returns {boolean} True if connection succeeded, false otherwise
 */
function connectValueBlock(block, valueBlock, entryKey) {
  try {
    const valueInput = block.getInput('VALUE') || block.getInput(entryKey);
    if (!valueInput) {
      console.warn(`No VALUE or ${entryKey} input found for block with key ${entryKey}`);
      return false;
    }

    if (!valueBlock || !valueBlock.outputConnection) {
      console.warn(`No valid output connection for value block of ${entryKey}`);
      return false;
    }

    const originalCheck = valueInput.connection.getCheck();
    valueInput.connection.setCheck(null);
    valueInput.connection.connect(valueBlock.outputConnection);
    valueInput.connection.setCheck(originalCheck);
    console.log(`Connected value block for ${entryKey}`);
    return true;
  } catch (error) {
    console.error(`Failed to connect value block for ${entryKey}:`, error);
    return false;
  }
}

// Register all blocks for this dictionary
export function registerBlocks(params = {}) {
  try {
    const filename = params.filename || 'controlDict';
    
    // Only register domain-specific blocks if the filename matches exactly
    if (filename === 'controlDict') {
      console.log(`Registering controlDict-specific blocks for ${filename}`);
      registerRootBlock(filename);
      registerKeywordBlocks(filename);
      registerFoamFileBlock(filename);
      console.log(`Registered ${filename} blocks:`, Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)));
    } else {
      console.log(`Skipping controlDict-specific blocks for ${filename}, using common blocks instead`);
    }
  } catch (error) {
    console.error(`Error registering blocks for ${params.filename || 'controlDict'}:`, error);
  }
}

// Register the root block
function registerRootBlock(filename) {
  const id = `${PREFIX}_dict`;
  if (Blockly.Blocks[id]) {
    console.log(`Root block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;  // Set filename on block
      this.jsonInit({
        type: id,
        message0: '%1 %2',
        args0: [
          {
            type: 'field_label',
            name: 'DICT_NAME',
            text: filename
          },
          {
            type: 'field_label',
            name: 'FILENAME',
            text: `(${filename})`
          }
        ],
        message1: '%1',
        args1: [
          {
            type: 'input_statement',
            name: 'ENTRIES',
            check: null
          }
        ],
        colour: BLOCK_COLOR,
        tooltip: `${filename} dictionary`,
        helpUrl: '',
      });
    },
  };
  
  console.log(`Registered root block: ${id} for ${filename}`);
}

// Register keyword blocks
function registerKeywordBlocks(filename) {
  // Register controlDict-specific number block
  const numberBlockType = `${PREFIX}_value_number`;
  if (!Blockly.Blocks[numberBlockType]) {
    Blockly.Blocks[numberBlockType] = {
      init: function() {
        this.filename = filename;  // Set filename on block
        this.jsonInit({
          type: numberBlockType,
          message0: '%1',
          args0: [
            {
              type: 'field_number',
              name: 'VALUE',
              value: 0,
              min: 0,
              validator: function(newValue) {
                const num = Number(newValue);
                return !isNaN(num) && num >= 0 ? num : 0;
              }
            }
          ],
          output: 'Number',
          colour: BLOCK_COLOR,
          tooltip: `Number value for ${filename}`,
          editable: true
        });
      }
    };
  }

  // Register the keyword blocks
  KEYWORD_CONFIGS.forEach(({ key, shadow, check, tooltip, validator, defaultValue }) => {
    const id = `${PREFIX}_${key}`;
    if (Blockly.Blocks[id]) {
      console.log(`Keyword block ${id} already registered`);
      return;
    }

    // Register a custom value block type for this key if it uses controlDict value string
    if (shadow === `${PREFIX}_value_string`) {
      const valueBlockType = `${PREFIX}_value_${key}`;
      if (!Blockly.Blocks[valueBlockType]) {
        Blockly.Blocks[valueBlockType] = {
          init: function() {
            this.filename = filename;  // Set filename on block
        this.jsonInit({
              type: valueBlockType,
              message0: '❝ %1 ❞',
          args0: [
            {
                  type: 'field_input',
              name: 'VALUE',
                  text: defaultValue,
                  validator: function(newValue) {
                    const validated = validator(newValue);
                    if (validated !== newValue) {
                      this.setFieldValue(validated, 'VALUE');
                    }
                    return validated;
                  }
                }
              ],
              output: 'String',
          colour: BLOCK_COLOR,
              tooltip: `${key} value (${tooltip}) for ${filename}`,
              editable: true
            });
          }
        };
      }
    }

    Blockly.Blocks[id] = {
      init: function() {
        this.filename = filename;  // Set filename on block
        this.jsonInit({
          type: id,
          message0: '%1 %2',
          args0: [
            {
              type: 'field_label',
              name: 'KEY',
              text: key
            },
            {
              type: 'input_value',
              name: 'VALUE',
              check: check,
              shadow: {
                type: check === 'Number' ? `${PREFIX}_value_number` : 
                      (shadow === `${PREFIX}_value_string` ? `${PREFIX}_value_${key}` : shadow),
                fields: {
                  VALUE: defaultValue
                },
                editable: true
              }
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: BLOCK_COLOR,
          tooltip: `${tooltip} (${filename})`,
          helpUrl: '',
          movable: true,
          deletable: true,
          editable: false,
          inputsInline: true,
          onchange: function(event) {
            if (event.type === Blockly.Events.BLOCK_MOVE) {
              const valueInput = this.getInput('VALUE');
              if (valueInput && !valueInput.connection.targetConnection) {
                // If value was disconnected, reconnect the shadow block
                const shadowBlock = this.workspace.newBlock(
                  check === 'Number' ? `${PREFIX}_value_number` :
                  (shadow === `${PREFIX}_value_string` ? `${PREFIX}_value_${key}` : shadow)
                );
                shadowBlock.filename = filename;  // Set filename on shadow block
                shadowBlock.setFieldValue(String(defaultValue), 'VALUE');
                shadowBlock.initSvg();
                shadowBlock.render();
                valueInput.connection.connect(shadowBlock.outputConnection);
              }
            }
          }
        });
      }
    };

    console.log(`Registered keyword block: ${id} for ${filename}`);
  });

  // Register a custom value string block type for controlDict
  const valueStringType = `${PREFIX}_value_string`;
  if (!Blockly.Blocks[valueStringType]) {
    Blockly.Blocks[valueStringType] = {
      init: function() {
        this.filename = filename;  // Set filename on block
        this.jsonInit({
          type: valueStringType,
          message0: '❝ %1 ❞',
          args0: [
            {
              type: 'field_input',
              name: 'VALUE',
              text: ''
            }
          ],
          output: 'String',
          colour: BLOCK_COLOR,
          tooltip: `String value for ${filename}`,
          editable: true
        });
      }
    };
  }
}

// Register FoamFile block
function registerFoamFileBlock(filename) {
  const id = `${PREFIX}_foamfile`;
  if (Blockly.Blocks[id]) {
    console.log(`FoamFile block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;  // Set filename on block
      this.jsonInit({
        type: id,
        message0: 'FoamFile',
        message1: 'version %1',
        args1: [
          {
            type: 'input_value',
            name: 'VERSION',
            check: 'String'
          }
        ],
        message2: 'format %1',
        args2: [
          {
            type: 'input_value',
            name: 'FORMAT',
            check: 'String'
          }
        ],
        message3: 'class %1',
        args3: [
          {
            type: 'input_value',
            name: 'CLASS',
            check: 'String'
          }
        ],
        message4: 'location %1',
        args4: [
          {
            type: 'input_value',
            name: 'LOCATION',
            check: 'String'
          }
        ],
        message5: 'object %1',
        args5: [
          {
            type: 'input_value',
            name: 'OBJECT',
            check: 'String'
          }
        ],
        previousStatement: null,
        nextStatement: null,
        colour: BLOCK_COLOR,
        tooltip: `FoamFile dictionary for ${filename}`,
        helpUrl: '',
      });

      // Set shadow blocks using DOMParser
      const fields = ['VERSION', 'FORMAT', 'CLASS', 'LOCATION', 'OBJECT'];
      const parser = new DOMParser();
      fields.forEach(field => {
        const input = this.getInput(field);
        if (input) {
          const shadowXmlString = `
            <shadow type="${PREFIX}_value_string">
              <field name="VALUE">${DEFAULT_FOAMFILE_ENTRIES[field.toLowerCase()]}</field>
            </shadow>
          `;
          const shadowXml = parser.parseFromString(shadowXmlString, 'text/xml').documentElement;
          input.connection.setShadowDom(shadowXml);
          // Set filename on shadow block
          const shadowBlock = input.connection.targetBlock();
          if (shadowBlock) {
            shadowBlock.filename = filename;
          }
        }
      });
    },
  };

  console.log(`Registered FoamFile block: ${id} for ${filename}`);
}

// Generate toolbox XML
function generateToolbox() {
  try {
  let xml = '<xml>';
    
    // Add FoamFile block with shadow blocks for each field
    xml += `<block type="${PREFIX}_foamfile">`;
    ['VERSION', 'FORMAT', 'CLASS', 'LOCATION', 'OBJECT'].forEach(field => {
      xml += `
        <value name="${field}">
          <shadow type="${PREFIX}_value_string">
            <field name="VALUE">${DEFAULT_FOAMFILE_ENTRIES[field.toLowerCase()]}</field>
          </shadow>
        </value>`;
    });
    xml += `</block>`;
  
  // Add root block
    xml += `<block type="${PREFIX}_dict"></block>`;
  
    // Add keyword blocks with appropriate shadow blocks
    KEYWORD_CONFIGS.forEach(({ key, shadow, defaultValue }) => {
    const id = `${PREFIX}_${key}`;
    xml += `
      <block type="${id}">
        <value name="VALUE">
            <shadow type="${shadow}">
              <field name="VALUE">${defaultValue}</field>
            </shadow>
        </value>
      </block>`;
  });
  
  xml += '</xml>';
  return xml;
  } catch (error) {
    console.error('Error generating toolbox:', error);
    return '<xml></xml>';
  }
}

/**
 * Find matching block type for a component
 * @param {Object} component - Component to match
 * @param {string} dictName - Name of the dictionary being processed
 * @returns {string|null} Block type or null if no match
 */
function findMatchingBlockType(component, dictName = 'controlDict') {
  try {
    // Only use domain-specific blocks if the dictionary name matches exactly
    if (dictName !== 'controlDict') {
      console.log(`Using common blocks for dictionary: ${dictName}`);
      return null;
    }

    const { type, name, key, value } = component;
    
    // Check if we have a configuration for this component type
    const typeConfig = COMPONENT_CONFIGS[type];
    if (!typeConfig) return null;

    // For dictionaries, check by name
    if (type === 'dictionary' && name) {
      const dictConfig = typeConfig[name];
      if (dictConfig) {
        return dictConfig.blockType;
      }
    }
    
    // For key-value pairs, check by key and use corresponding value configuration
    if (type === 'keyValue' && key) {
      const keyConfig = typeConfig[key];
      if (keyConfig) {
        // Find the keyword config to get the value type
        const keywordConfig = KEYWORD_CONFIGS.find(cfg => cfg.key === key);
        if (keywordConfig) {
          // Store the value configuration in the block type for later use
          return {
            blockType: keyConfig.blockType,
            valueType: keywordConfig.check === 'Number' ? `${PREFIX}_value_number` : keywordConfig.shadow,
            valueCheck: keywordConfig.check
          };
        }
        return keyConfig.blockType;
      }
    }

    // For value objects without a name, try to match by key
    if (type === 'value' && !name && key) {
      const keyConfig = COMPONENT_CONFIGS.keyValue[key];
      if (keyConfig) {
        const keywordConfig = KEYWORD_CONFIGS.find(cfg => cfg.key === key);
        if (keywordConfig) {
          return {
            blockType: keywordConfig.check === 'Number' ? `${PREFIX}_value_number` : keywordConfig.shadow,
            valueCheck: keywordConfig.check
          };
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error finding matching block type:', error);
    return null;
  }
}

/**
 * Create a block from a component with matching priority
 * @param {Object} component - Component to create block from
 * @param {Blockly.Workspace} workspace - Target workspace
 * @param {string} dictName - Name of the dictionary being processed
 * @returns {Blockly.Block|null} Created block or null
 */
function createBlockFromComponent(component, workspace, dictName = 'controlDict') {
  try {
    // First try to find a matching controlDict-specific block
    const matchingResult = findMatchingBlockType(component, dictName);
    if (matchingResult) {
      const blockType = typeof matchingResult === 'string' ? matchingResult : matchingResult.blockType;
      console.log(`Using matching block type: ${blockType}`);
      const block = createBlock(workspace, blockType);
      
      if (block && typeof matchingResult !== 'string') {
        // If we have value configuration, set up the value input
        if (matchingResult.valueType) {
          const valueInput = block.getInput('VALUE');
          if (valueInput) {
            const shadowBlock = workspace.newBlock(matchingResult.valueType);
            shadowBlock.initSvg();
            shadowBlock.render();
            valueInput.connection.connect(shadowBlock.outputConnection);
          }
        }
      }
      
      return block;
    }

    // If no match found or not a controlDict, use common blocks
    console.log(`Using common blocks for dictionary: ${dictName}`);
    const commonBlockType = component.type === 'dictionary' ? 'common_dict' :
                           component.type === 'keyValue' ? 'common_entry' :
                           'common_value_string';
    return createBlock(workspace, commonBlockType);
  } catch (error) {
    console.error('Error creating block from component:', error);
    return null;
  }
}

// Update createControlDictBlock to pass the dictionary name
export function createControlDictBlock(workspace, entries = [], dictName = 'controlDict') {
  try {
    console.log(`Creating block for dictionary: ${dictName}`);
    
    // Use common dict block if not controlDict
    const blockType = dictName === 'controlDict' ? `${PREFIX}_dict` : 'common_dict';
    const block = createBlock(workspace, blockType);
    if (!block) {
      console.error(`Failed to create ${dictName} block`);
      return null;
    }

    const entriesInput = block.getInput('ENTRIES');
    if (entriesInput && entries.length > 0) {
      let prevBlock = null;
      
      for (const entry of entries) {
        const entryBlock = createBlockFromComponent(entry, workspace, dictName);
        if (entryBlock) {
          // Set values for key-value pairs
          if (entry.type === 'keyValue') {
            if (entryBlock.getField('KEY')) {
              entryBlock.setFieldValue(entry.key, 'KEY');
            }
            
            // Set value if it's a simple value
            if (typeof entry.value !== 'object') {
              const valueBlock = createBlock(workspace, 
                typeof entry.value === 'number' ? 'common_value_number' : 'common_value_string');
              if (valueBlock) {
                valueBlock.setFieldValue(String(entry.value), 'VALUE');
                connectValueBlock(entryBlock, valueBlock, entry.key);
              }
            }
          }

          // Connect blocks
          if (!prevBlock) {
            entriesInput.connection.connect(entryBlock.previousConnection);
          } else if (prevBlock.nextConnection && entryBlock.previousConnection) {
            prevBlock.nextConnection.connect(entryBlock.previousConnection);
          }
          prevBlock = entryBlock;
        }
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating block for ${dictName}:`, error);
    return null;
  }
}

// Update createKeywordBlock to use validators
export function createKeywordBlock(workspace, key, value) {
  try {
    console.log(`Creating keyword block for ${key}:`, value);
    
    const config = KEYWORD_CONFIGS.find(cfg => cfg.key === key);
    if (!config) {
      console.error(`No configuration found for keyword: ${key}`);
      return null;
    }

    const block = createBlock(workspace, `${PREFIX}_${key}`);
    if (!block) {
      console.error(`Failed to create block for keyword: ${key}`);
      return null;
    }

    let valueBlock;
    const validatedValue = config.validator(value);
    
    if (config.shadow === `${PREFIX}_value_string`) {
      valueBlock = createBlock(workspace, `${PREFIX}_value_${key}`);
      if (valueBlock) {
        valueBlock.setFieldValue(String(validatedValue), 'VALUE');
      }
    } else {
      valueBlock = createBlock(workspace, config.shadow);
      if (valueBlock) {
        valueBlock.setFieldValue(String(validatedValue), 'VALUE');
      }
    }

    if (valueBlock) {
      connectValueBlock(block, valueBlock, key);
    }

    return block;
  } catch (error) {
    console.error(`Error creating keyword block for ${key}:`, error);
    return null;
  }
}
